import { Box } from "@repo/components/src/ui/box";
import { HStack } from "@repo/components/src/ui/hstack";
import { Icon } from "@repo/components/src/ui/icon";
import { Pressable } from "@repo/components/src/ui/pressable";
import { Text } from "@repo/components/src/ui/text";
import { VStack } from "@repo/components/src/ui/vstack";
import { Pause, Play } from "lucide-react-native";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

type AudioMessageProps = {
  uri: string;
  name: string;
  position: "left" | "right";
};

const BAR_COUNT = 28;

const formatSeconds = (seconds: number) => {
  if (!isFinite(seconds) || seconds < 0) return "0:00";
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${s.toString().padStart(2, "0")}`;
};

export default function AudioMessage(props: AudioMessageProps) {
  const isRight = props.position === "right";
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [playing, setPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);

  const bars = useMemo(
    () =>
      Array.from({ length: BAR_COUNT }, (_, i) => {
        const base = 0.35 + Math.abs(Math.sin((i + 1) * 1.35)) * 0.6;
        return Math.round(base * 100) / 100;
      }),
    [],
  );

  useEffect(() => {
    const a = audioRef.current;
    if (!a) return;
    const onTime = () => setCurrentTime(a.currentTime);
    const onLoaded = () => setDuration(a.duration);
    const onEnd = () => {
      setPlaying(false);
      setCurrentTime(0);
    };
    a.addEventListener("timeupdate", onTime);
    a.addEventListener("loadedmetadata", onLoaded);
    a.addEventListener("durationchange", onLoaded);
    a.addEventListener("ended", onEnd);
    return () => {
      a.removeEventListener("timeupdate", onTime);
      a.removeEventListener("loadedmetadata", onLoaded);
      a.removeEventListener("durationchange", onLoaded);
      a.removeEventListener("ended", onEnd);
    };
  }, []);

  const togglePlay = useCallback(() => {
    const a = audioRef.current;
    if (!a) return;
    if (a.paused) {
      a.play();
      setPlaying(true);
    } else {
      a.pause();
      setPlaying(false);
    }
  }, []);

  const progress = duration ? currentTime / duration : 0;

  const playTileClass = isRight
    ? "bg-background-0/20 text-background-0"
    : "bg-brand-500 text-background-0 shadow-[0_6px_14px_-4px_rgba(124,58,237,0.5)]";
  const inactiveBarClass = isRight
    ? "bg-background-0/30"
    : "bg-typography-300";
  const activeBarClass = isRight ? "bg-background-0" : "bg-brand-500";
  const timeClass = isRight ? "text-background-0/80" : "text-typography-500";

  return (
    <VStack space="xs" className="min-w-[260px] max-w-sm py-1">
      <HStack space="sm" className="items-center">
        <Pressable
          onPress={togglePlay}
          className={`p-2.5 rounded-full ${playTileClass}`}
        >
          <Icon as={playing ? Pause : Play} className="w-4 h-4" />
        </Pressable>
        <HStack space="xs" className="flex-1 items-center h-8">
          {bars.map((h, i) => {
            const active = i / BAR_COUNT < progress;
            return (
              <Box
                key={i}
                className={`w-0.5 rounded-full ${
                  active ? activeBarClass : inactiveBarClass
                }`}
                style={{ height: `${Math.round(h * 100)}%` }}
              />
            );
          })}
        </HStack>
        <Text size="2xs" className={`${timeClass} tabular-nums tracking-wider`}>
          {formatSeconds(playing || currentTime > 0 ? currentTime : duration)}
        </Text>
      </HStack>
      <audio
        ref={audioRef}
        src={props.uri}
        preload="metadata"
        style={{ display: "none" }}
      />
    </VStack>
  );
}
